import { RefObject, useLayoutEffect } from 'react'

/**
 * Promotes a portalled overlay into the browser's top layer so host-site
 * chrome — sticky headers, chat widgets, cookie bars — can never paint above
 * it. Those third-party elements routinely use near-maximum z-index values
 * (Intercom, Drift and friends sit in the 2147483000+ range), so competing on
 * z-index alone is unwinnable. Top-layer content renders above the entire
 * page regardless of z-index, and stacks by promotion order, so a popover
 * opened from inside a modal still lands on top.
 *
 * The Popover API is used purely as the promotion mechanism, in `manual` mode:
 * it never light-dismisses, so open/close, focus and Escape handling remain
 * entirely with the calling component.
 *
 * Browsers without Popover API support (pre-Chrome 114 / Safari 17 /
 * Firefox 125) keep the element in normal flow and fall back to the very high
 * z-index set on the element itself.
 */
export function useTopLayer (
	ref: RefObject<HTMLElement | null>,
	active = true
): void {
	useLayoutEffect(() => {
		const el = ref.current
		if (!active || !el) return
		if (typeof el.showPopover !== 'function') return

		// UA styles hide `[popover]` until it is shown, so if the call fails the
		// attribute must come off again or the overlay would never render.
		el.setAttribute('popover', 'manual')
		try {
			el.showPopover()
		} catch {
			el.removeAttribute('popover')
			return
		}

		return () => {
			try {
				el.hidePopover()
			} catch {
				// Already detached — nothing left to demote.
			}
			el.removeAttribute('popover')
		}
	}, [ref, active])
}
